/**
 * FICHE ARTICLE → ONGLET « CHAMPS PERSO » — la moitié qui REMPLIT (le référentiel définit, lui).
 *
 * Transposé du relevé maquette (`fT7`) : une ligne par champ défini (libellé + type en mono), le
 * contrôle adapté au type — texte, nombre aligné à droite, date, bascule Oui/Non, liste — le bouton
 * « Gérer dans les référentiels » (qui arrive sur le BON onglet), et l'état vide honnête qui renvoie
 * vers la configuration.
 *
 * ⚠️ RIEN NE PART D'ICI : ce panneau RAPPORTE ses modifications à la fiche (`onEdit`), qui les tient
 * dans la MÊME barre Annuler/Enregistrer que le reste — c'est le circuit de la maquette (`cfSet` →
 * `fDirty`). La première version enregistrait au blur, sans confirmation : elle divergeait de la
 * maquette ET du reste de la fiche (constaté par l'utilisateur, 2026-08-24). `onEdit(id, undefined)`
 * = « revenu à l'origine », la modification disparaît de la barre.
 */
import { useEffect, useState } from 'react';
import { Trans } from '@orgazone/ui-web';
import { useNavigate } from 'react-router-dom';
import { api, type CustomField, type CustomValues } from '../api';
import { t } from '../i18n';
import { libelleDeType } from './referentiels-champs';

/** Une modification en attente : la valeur à envoyer (null = effacer), `undefined` = pas de modification. */
export type PersoEdit = string | number | boolean | null;

export function ChampsPerso({ modelKey, edits, onEdit, version, editable }: {
  modelKey: string;
  edits: Record<string, PersoEdit>;
  onEdit: (fieldId: string, v: PersoEdit | undefined) => void;
  /** Incrémenté par la fiche après un enregistrement — déclenche la relecture des valeurs SERVIES. */
  version: number;
  /**
   * Le compte a-t-il le droit d'écrire dans ce magasin ?
   * ⚠️ CE PANNEAU ÉTAIT LE SEUL À NE PAS LE RECEVOIR (correctif d'audit 2026-08-25) — Général, Prix,
   * Photos et Déclinaisons l'ont tous. Ses champs restaient donc saisissables en lecture seule : dès la
   * première frappe, le pied de fiche remplaçait « Lecture seule : votre compte n'a pas le droit
   * d'écrire » par « Modifications non enregistrées » et sortait un « Enregistrer » qui finissait en
   * 403. La garde serveur tenait — c'est l'ÉCRAN qui promettait ce qu'il ne pouvait pas tenir (§23.0 :
   * option offerte non implémentée, et bouton qui fait semblant).
   */
  editable: boolean;
}) {
  const nav = useNavigate();
  const [fields, setFields] = useState<CustomField[] | null>(null);
  const [values, setValues] = useState<CustomValues>({});
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    setFailure(null);
    api.articleCustomValues(modelKey)
      .then((r) => { if (vivant) { setFields(r.fields); setValues(r.values); } })
      .catch((e: unknown) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [modelKey, version]);

  /** La valeur AFFICHÉE : la modification en attente si elle existe, sinon la valeur servie. */
  const displayed = (id: string): PersoEdit | undefined =>
    Object.prototype.hasOwnProperty.call(edits, id) ? edits[id] : (values[id] as PersoEdit | undefined);

  /** Rapporte une saisie — ou la retire si elle revient à la valeur servie (plus rien à enregistrer). */
  const rapporter = (c: CustomField, v: PersoEdit) => {
    const servie = (values[c.id] as PersoEdit | undefined) ?? null;
    // ⚠️ L'égalité se juge sur la valeur NORMALISÉE : « 2,50 » ressaisi sur un 2.5 servi n'est pas une
    // modification ; et un booléen ABSENT vaut « Non » — le double clic ne fabrique pas un `false` stocké.
    const egales = c.fieldType === 'num' && typeof v === 'string'
      ? Number(v.trim().replace(',', '.')) === servie
      : c.fieldType === 'bool'
        ? (v === true) === (servie === true)
        : (v ?? null) === servie;
    onEdit(c.id, egales ? undefined : v);
  };

  if (failure) {
    return (
      <p role="alert" style={{ margin: 0, padding: '18px 2px', color: 'var(--danger-ink)', fontSize: 'var(--t-body)', lineHeight: 1.6 }}>
        
        {t('Les champs personnalisés n’ont pas pu être lus : {failure}. Ce n’est pas « aucun champ ».', { failure })}
      </p>
    );
  }
  if (!fields) return <p style={{ margin: 0, padding: '18px 2px', color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>{t('Lecture des champs personnalisés…')}</p>;

  const CHAMP_STYLE = {
    width: '100%', height: 38, padding: '0 11px', border: '1px solid var(--ink-200)',
    borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)',
    fontSize: 'var(--t-lead)', outline: 'none', boxSizing: 'border-box' as const,
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Champs personnalisés')}</div>
          <p style={{ margin: '3px 0 0', fontSize: '12.5px', color: 'var(--ink-500)' }}>
            
            {t('Champs définis pour toute l’enseigne dans les référentiels — la valeur saisie ici reste propre à cet article.')}
          </p>
        </div>
        <button type="button" onClick={() => nav('/referentiels?tab=champs')} className="oz-cf-ref"
          style={{ height: 36, padding: '0 14px', borderRadius: 'var(--r-ctrl)', fontSize: '12.5px', fontWeight: 600, flexShrink: 0, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
          
          {t('Gérer dans les référentiels')}
        </button>
      </div>

      {fields.length === 0 ? (
        <div style={{ padding: '34px 20px', textAlign: 'center', border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-md)' }}>
          <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Aucun champ personnalisé configuré')}</div>
          <p style={{ margin: '4px auto 0', fontSize: '12.5px', color: 'var(--ink-500)', maxWidth: 420, lineHeight: 1.55 }}>
            <Trans k='La configuration se fait une fois pour toute l’enseigne, dans <1>Référentiels → Champs personnalisés</1>. Les champs créés apparaîtront ici sur toutes les fiches.' parts={[(x) => <strong>{x}</strong>]} /></p>
        </div>
      ) : (
        <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
          {fields.map((c) => {
            const v = displayed(c.id);
            return (
              <div key={c.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(140px,1fr) minmax(180px,260px)', gap: 12, padding: '12px 16px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)' }}>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)' }}>{c.label}</span>
                  <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-400)', marginTop: 2 }}>{libelleDeType(c.fieldType)}</span>
                </span>
                <span>
                  {c.fieldType === 'txt' ? (
                    <input aria-label={c.label} value={typeof v === 'string' ? v : ''} placeholder="—" readOnly={!editable}
                      onChange={(e) => rapporter(c, e.target.value || null)}
                      style={CHAMP_STYLE} />
                  ) : c.fieldType === 'num' ? (
                    // La saisie reste TELLE QUELLE dans la barre (« 2,50 ») : c'est le serveur qui la
                    // normalise à l'enregistrement — et qui la refuse, nommée, si elle n'est pas un nombre.
                    <input aria-label={c.label} value={v == null ? '' : typeof v === 'number' ? String(v).replace('.', ',') : String(v)} placeholder="0" inputMode="decimal" readOnly={!editable}
                      onChange={(e) => rapporter(c, e.target.value.trim() || null)}
                      style={{ ...CHAMP_STYLE, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} />
                  ) : c.fieldType === 'date' ? (
                    <input type="date" aria-label={c.label} value={typeof v === 'string' ? v : ''} readOnly={!editable}
                      onChange={(e) => rapporter(c, e.target.value || null)}
                      style={CHAMP_STYLE} />
                  ) : c.fieldType === 'bool' ? (
                    <button type="button" onClick={() => { if (editable) rapporter(c, !(v === true)); }}
                      aria-disabled={!editable || undefined}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 9, height: 38, padding: '0 4px', border: 'none', background: 'transparent', cursor: editable ? 'pointer' : 'default' }}>
                      <span style={{ width: 38, height: 22, borderRadius: 'var(--r-pill)', background: v === true ? 'var(--teal-500)' : 'var(--ink-200)', display: 'flex', alignItems: 'center', justifyContent: v === true ? 'flex-end' : 'flex-start', padding: '0 3px' }}>
                        <span style={{ width: 16, height: 16, borderRadius: 'var(--r-pill)', background: '#fff' }} />
                      </span>
                      <span style={{ fontSize: '12.5px', fontWeight: 600, color: v === true ? 'var(--teal-800)' : 'var(--ink-500)' }}>{v === true ? t('Oui') : t('Non')}</span>
                    </button>
                  ) : (
                    <select aria-label={c.label} value={typeof v === 'string' ? v : ''} disabled={!editable}
                      onChange={(e) => rapporter(c, e.target.value || null)}
                      style={{ ...CHAMP_STYLE, padding: '0 9px' }}>
                      <option value="">—</option>
                      {c.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.55 }}>
        
        {t('Les valeurs vivent dans un JSON par article, dans la table dédiée aux champs personnalisés — la configuration des champs se gère dans les référentiels de l’enseigne.')}
      </div>
    </div>
  );
}
